import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Star, Trophy, Users, TrendingUp, Loader2, Save, Plus, Trash2, RefreshCw, Award } from 'lucide-react';

const TIER_COLORS = ['#CD7F32', '#94A3B8', '#F59E0B', '#60A5FA'];

export default function LoyaltyProgram() {
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('config');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cR, mR, tR] = await Promise.all([api.get('/loyalty/config'), api.get('/loyalty/members'), api.get('/loyalty/transactions')]);
      setConfig(cR.data);
      setMembers(mR.data || []);
      setTransactions(tR.data || []);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try { await api.patch('/loyalty/config', config); toast.success('Configuración guardada'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const addTier = () => setConfig(c => ({
    ...c, tiers: [...c.tiers, { name: 'Nuevo nivel', min_points: 0, color: '#64748b', discount: 0 }]
  }));
  const removeTier = (i) => setConfig(c => ({ ...c, tiers: c.tiers.filter((_, idx) => idx !== i) }));
  const updateTier = (i, key, val) => setConfig(c => ({ ...c, tiers: c.tiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t) }));

  const getTier = (points) => {
    if (!config) return null;
    const sorted = [...config.tiers].sort((a, b) => b.min_points - a.min_points);
    return sorted.find(t => points >= t.min_points);
  };

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="Programa de Fidelización" helpSection="loyalty" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  const totalPoints = members.reduce((s, m) => s + (m.points || 0), 0);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Programa de Fidelización" helpSection="loyalty" actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} className="h-8"><RefreshCw className="h-3.5 w-3.5" /></Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar
          </Button>
        </div>
      } />

      <div className="flex-1 p-6 max-w-5xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center">
              <Star className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Programa de Fidelización</h1>
              <p className="text-sm text-slate-500">Premia a tus clientes más leales con puntos y descuentos</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-sm text-slate-600">Programa activo</Label>
            <Switch checked={!!config?.enabled} onCheckedChange={v => setConfig(c => ({ ...c, enabled: v }))} />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { l: 'Miembros activos', v: members.length, icon: Users, c: 'text-blue-600', bg: 'bg-blue-50' },
            { l: 'Puntos totales', v: totalPoints.toLocaleString(), icon: Star, c: 'text-amber-600', bg: 'bg-amber-50' },
            { l: 'Transacciones', v: transactions.length, icon: TrendingUp, c: 'text-green-600', bg: 'bg-green-50' },
            { l: 'Niveles activos', v: config?.tiers?.length || 0, icon: Trophy, c: 'text-purple-600', bg: 'bg-purple-50' },
          ].map(s => (
            <Card key={s.l} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center mb-3", s.bg)}>
                  <s.icon className={cn("h-4 w-4", s.c)} />
                </div>
                <div className="text-2xl font-bold text-slate-800">{s.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.l}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[{ k: 'config', l: 'Configuración' }, { k: 'tiers', l: 'Niveles' }, { k: 'members', l: 'Miembros' }].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all",
                tab === t.k ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700")}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === 'config' && config && (
          <div className="grid grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm">Acumulación de puntos</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Puntos por visita</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} value={config.points_per_visit}
                      onChange={e => setConfig(c => ({ ...c, points_per_visit: +e.target.value }))} className="text-sm max-w-[120px]" />
                    <span className="text-sm text-slate-500">puntos por cada visita</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Puntos por euro gastado</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} step={0.1} value={config.points_per_euro}
                      onChange={e => setConfig(c => ({ ...c, points_per_euro: +e.target.value }))} className="text-sm max-w-[120px]" />
                    <span className="text-sm text-slate-500">puntos por €1</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm">Ejemplo de beneficios</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {config.tiers?.map((tier, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: tier.color + '15', border: `1.5px solid ${tier.color}30` }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: tier.color, color: '#fff' }}>
                      {tier.name[0]}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold" style={{ color: tier.color }}>{tier.name}</p>
                      <p className="text-[11px] text-slate-500">Desde {tier.min_points.toLocaleString()} puntos · {tier.discount}% descuento</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {tab === 'tiers' && config && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={addTier} className="h-8 text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" /> Agregar nivel
              </Button>
            </div>
            {config.tiers?.map((tier, i) => (
              <Card key={i} className="border-0 shadow-sm overflow-hidden">
                <div className="h-1.5" style={{ backgroundColor: tier.color }} />
                <CardContent className="p-4">
                  <div className="grid grid-cols-5 gap-4 items-end">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Nombre del nivel</Label>
                      <Input value={tier.name} onChange={e => updateTier(i, 'name', e.target.value)} className="text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Puntos mínimos</Label>
                      <Input type="number" min={0} value={tier.min_points} onChange={e => updateTier(i, 'min_points', +e.target.value)} className="text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Descuento (%)</Label>
                      <Input type="number" min={0} max={100} value={tier.discount} onChange={e => updateTier(i, 'discount', +e.target.value)} className="text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Color</Label>
                      <input type="color" value={tier.color} onChange={e => updateTier(i, 'color', e.target.value)}
                        className="h-8 w-full rounded border border-slate-200 cursor-pointer p-0.5" />
                    </div>
                    <div className="flex justify-end">
                      <button onClick={() => removeTier(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {tab === 'members' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {members.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <Award className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Sin miembros aún</p>
                  <p className="text-sm mt-1">Los clientes que acumulen puntos aparecerán aquí</p>
                </div>
              ) : (
                <div className="divide-y">
                  <div className="grid grid-cols-5 gap-4 px-4 py-3 bg-slate-50 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    <span>Cliente</span><span>Puntos</span><span>Nivel</span><span>Visitas</span><span>Último canje</span>
                  </div>
                  {members.map((m, i) => {
                    const tier = getTier(m.points || 0);
                    return (
                      <div key={i} className="grid grid-cols-5 gap-4 px-4 py-3 hover:bg-slate-50 items-center text-sm">
                        <div>
                          <p className="font-medium text-slate-800">{m.name}</p>
                          <p className="text-[11px] text-slate-400">{m.email}</p>
                        </div>
                        <span className="font-bold text-amber-600">{(m.points || 0).toLocaleString()}</span>
                        <span>
                          {tier && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: tier.color + '20', color: tier.color }}>{tier.name}</span>}
                        </span>
                        <span className="text-slate-600">{m.visits || 0}</span>
                        <span className="text-slate-400 text-xs">{m.last_redemption ? new Date(m.last_redemption).toLocaleDateString('es-ES') : '—'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
